import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Building2, Gem, Palette, TrendingUp, Truck, Banknote, Menu, X, ChevronDown, Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Building2: Building2,
  Gem: Gem,
  Palette: Palette,
  TrendingUp: TrendingUp,
  Truck: Truck,
  Banknote: Banknote
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#contact", label: "Contact" }];


  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? "glass py-4" : "bg-transparent py-6"}`
      }>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#" data-testid="logo-link" className="flex items-center gap-3">
            <div className="monogram-border">
              <span className="monogram text-xl text-white">B | F</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-12">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="nav-link font-medium">

                {link.label}
              </a>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>

            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen &&
        <div
          data-testid="mobile-menu"
          className="md:hidden mt-6 pb-6 border-t border-white/10 pt-6">

            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link font-medium"
              onClick={() => setIsMobileMenuOpen(false)}>

                  {link.label}
                </a>
            )}
            </div>
          </div>
        }
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () => {
  const [logoError, setLogoError] = useState(false);
  
  return (
    <section
      data-testid="hero-section"
      className="hero-bg min-h-screen flex items-center justify-center relative">

      <div className="relative z-10 text-center px-6 md:px-12 max-w-5xl mx-auto">
        {/* Company Logo */}
        <div className="fade-in-up mb-12">
          {!logoError ? (
            <img
              src="/logo.png"
              alt="Bumberg Family Consulting Inc. Logo"
              className="mx-auto w-64 md:w-80 lg:w-96"
              data-testid="company-logo"
              loading="eager"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="mx-auto w-64 md:w-80 lg:w-96 py-8">
              <div className="border border-white/30 px-8 py-6 inline-block mb-4">
                <span className="font-serif text-4xl md:text-5xl text-white tracking-wider">B | F</span>
              </div>
              <h1 className="text-2xl md:text-3xl text-white font-serif tracking-wide">
                BUMBERG FAMILY
              </h1>
              <p className="text-sm md:text-base text-white/70 tracking-[0.3em] mt-2">
                CONSULTING INC.
              </p>
            </div>
          )}
        </div>

        <p
          className="fade-in-up-delay-2 text-base md:text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-12 leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif" }}>

          Trusted advisors in real estate, fine jewelry, art, sales & distribution, 
          logistics, and asset liquidation. Excellence through expertise.
        </p>

        <div className="fade-in-up-delay-3 flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#services" className="btn-primary" data-testid="explore-services-btn">
            Explore Services
          </a>
          <a href="#contact" className="btn-secondary" data-testid="get-in-touch-btn">
            Get in Touch
          </a>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 fade-in-up-delay-3">
          <a href="#about" className="flex flex-col items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors duration-300">
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <ChevronDown size={20} className="animate-bounce" />
          </a>
        </div>
      </div>
    </section>);

};

// About Section
const AboutSection = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#050505]">

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="line-separator mb-8"></div>
            <h2
              className="text-4xl md:text-5xl text-[#F5F5F5] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}>

              Decades of
              <span className="gold-text block">Trusted Expertise</span>
            </h2>
            <p
              className="text-[#A3A3A3] text-base md:text-lg leading-relaxed mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}>

              Bumberg Family Consulting Inc. has established itself as a premier 
              consulting firm, delivering exceptional advisory services across 
              diverse asset classes. Our commitment to excellence and discretion 
              has earned the trust of discerning clients worldwide.
            </p>
            <p
              className="text-[#A3A3A3] text-base md:text-lg leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif" }}>

              With deep expertise spanning real estate, fine jewelry, art, 
              sales & distribution, logistics, and asset liquidation, we provide 
              comprehensive solutions tailored to each client's unique requirements.
            </p>
          </div>
          <div className="relative">
            <div className="aspect-square bg-[#0A0A0A] border border-white/5 p-12 flex items-center justify-center">
              <div className="text-center">
                <div className="monogram-border inline-block mb-8">
                  <span className="monogram text-5xl text-white">B | F</span>
                </div>
                <p
                  className="text-[#A3A3A3] text-sm uppercase tracking-widest"
                  style={{ fontFamily: "'Manrope', sans-serif" }}>

                  Established Excellence
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Service Card Component
const ServiceCard = ({ service }) => {
  const IconComponent = iconMap[service.icon] || Building2;

  return (
    <div
      data-testid={`service-card-${service.id}`}
      className="service-card bg-[#0A0A0A] border border-white/5 p-8 hover:border-[#D4AF37]/50 transition-all duration-500 group card-border-animate"
      style={{ backgroundImage: `url(${service.image})` }}>

      <div className="service-card-content">
        <div className="mb-6 text-[#D4AF37] group-hover:scale-110 transition-transform duration-500">
          <IconComponent size={32} strokeWidth={1.5} />
        </div>
        <h3
          className="text-2xl text-[#F5F5F5] mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}>

          {service.title}
        </h3>
        <p
          className="text-sm leading-relaxed text-[#A3A3A3]"
          style={{ fontFamily: "'Manrope', sans-serif" }}>

          {service.description}
        </p>
      </div>
    </div>);

};

// Services Section
const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
        // Fallback services
        setServices([
        {
          id: "real-estate",
          title: "Real Estate",
          description: "Premium real estate advisory services including property valuation, acquisition strategies, portfolio management, and investment consulting.",
          icon: "Building2",
          image: ""
        },
        {
          id: "jewelry",
          title: "Jewelry",
          description: "Expert jewelry consulting including authentication, appraisal services, acquisition guidance, and estate jewelry evaluation.",
          icon: "Gem",
          image: ""
        },
        {
          id: "art",
          title: "Art",
          description: "Comprehensive art advisory services encompassing collection building, authentication, market analysis, and auction representation.",
          icon: "Palette",
          image: ""
        },
        {
          id: "sales-distribution",
          title: "Sales & Distribution",
          description: "Strategic sales and distribution consulting for luxury goods and high-value assets, including channel development.",
          icon: "TrendingUp",
          image: ""
        },
        {
          id: "logistics",
          title: "Logistics",
          description: "Specialized logistics solutions for high-value items including secure transportation and customs facilitation.",
          icon: "Truck",
          image: ""
        },
        {
          id: "liquidation",
          title: "Liquidation Assets",
          description: "Professional asset liquidation services including estate liquidation and strategic asset disposition.",
          icon: "Banknote",
          image: ""
        }]
        );
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="line-separator mx-auto mb-8"></div>
          <h2
            className="text-4xl md:text-5xl text-[#F5F5F5] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}>

            Our <span className="gold-text">Services</span>
          </h2>
          <p
            className="text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto"
            style={{ fontFamily: "'Manrope', sans-serif" }}>

            Comprehensive consulting solutions across diverse asset classes,
            delivered with discretion and excellence.
          </p>
        </div>

        {loading ?
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) =>
          <div key={i} className="bg-[#0A0A0A] border border-white/5 p-8 animate-pulse">
                <div className="w-8 h-8 bg-white/10 rounded mb-6"></div>
                <div className="h-6 bg-white/10 rounded mb-4 w-3/4"></div>
                <div className="h-20 bg-white/10 rounded"></div>
              </div>
          )}
          </div> :

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) =>
          <ServiceCard key={service.id} service={service} />
          )}
          </div>
        }
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service_interest: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you for your inquiry. We will be in touch shortly.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        service_interest: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("There was an error submitting your inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#050505]">

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <div className="line-separator mb-8"></div>
            <h2
              className="text-4xl md:text-5xl text-[#F5F5F5] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}>

              Get in <span className="gold-text">Touch</span>
            </h2>
            <p
              className="text-[#A3A3A3] text-base md:text-lg leading-relaxed mb-12"
              style={{ fontFamily: "'Manrope', sans-serif" }}>

              We welcome inquiries from clients seeking exceptional advisory 
              services. Reach out to discuss how we can assist with your unique requirements.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="text-[#D4AF37] mt-1">
                  <Phone size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h4
                    className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-1"
                    style={{ fontFamily: "'Manrope', sans-serif" }}>

                    Phone
                  </h4>
                  <p className="text-[#A3A3A3]" style={{ fontFamily: "'Manrope', sans-serif" }}>+1 (818) 585-3550

                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-[#D4AF37] mt-1">
                  <Mail size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h4
                    className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-1"
                    style={{ fontFamily: "'Manrope', sans-serif" }}>

                    Email
                  </h4>
                  <p className="text-[#A3A3A3]" style={{ fontFamily: "'Manrope', sans-serif" }}>maxwellbumberg@gmail.com

                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="text-[#D4AF37] mt-1">
                  <MapPin size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h4
                    className="text-[#F5F5F5] text-sm uppercase tracking-widest mb-1"
                    style={{ fontFamily: "'Manrope', sans-serif" }}>

                    Office
                  </h4>
                  <p className="text-[#A3A3A3]" style={{ fontFamily: "'Manrope', sans-serif" }}>Miami, FL

                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-12">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                    data-testid="contact-name-input"
                    className="custom-input" />

                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    required
                    data-testid="contact-email-input"
                    className="custom-input" />

                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number (Optional)"
                    data-testid="contact-phone-input"
                    className="custom-input" />

                </div>

                <div>
                  <select
                    name="service_interest"
                    value={formData.service_interest}
                    onChange={handleInputChange}
                    data-testid="contact-service-select"
                    className="custom-input cursor-pointer"
                    style={{ color: formData.service_interest ? '#F5F5F5' : 'rgba(255, 255, 255, 0.3)' }}>

                    <option value="" style={{ background: '#0A0A0A' }}>Service of Interest (Optional)</option>
                    <option value="real-estate" style={{ background: '#0A0A0A', color: '#F5F5F5' }}>Real Estate</option>
                    <option value="jewelry" style={{ background: '#0A0A0A', color: '#F5F5F5' }}>Jewelry</option>
                    <option value="art" style={{ background: '#0A0A0A', color: '#F5F5F5' }}>Art</option>
                    <option value="sales-distribution" style={{ background: '#0A0A0A', color: '#F5F5F5' }}>Sales & Distribution</option>
                    <option value="logistics" style={{ background: '#0A0A0A', color: '#F5F5F5' }}>Logistics</option>
                    <option value="liquidation" style={{ background: '#0A0A0A', color: '#F5F5F5' }}>Liquidation Assets</option>
                  </select>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Your Message"
                    required
                    rows={4}
                    data-testid="contact-message-input"
                    className="custom-input resize-none" />

                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">

                  {isSubmitting ?
                  "Sending..." :

                  <>
                      Send Inquiry
                      <ArrowRight size={16} />
                    </>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

// Footer Section
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      data-testid="footer"
      className="py-16 px-6 md:px-12 lg:px-24 bg-[#050505] border-t border-white/5">

      <div className="max-w-7xl mx-auto text-center">
        <div className="mb-8">
          <h3
            className="text-2xl md:text-3xl text-[#F5F5F5]"
            style={{ fontFamily: "'Playfair Display', serif" }}>

            Bumberg Family Consulting Inc.
          </h3>
        </div>
        <p
          className="text-[#A3A3A3] text-sm"
          style={{ fontFamily: "'Manrope', sans-serif" }}>

          &copy; {currentYear} Bumberg Family Consulting Inc. All rights reserved.
        </p>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay"></div>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            color: '#F5F5F5',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }} />

      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ContactSection />
      <Footer />
    </div>);

}

export default App;